import { CommonModule, DOCUMENT } from '@angular/common';
import { CdkTrapFocus, InteractivityChecker } from '@angular/cdk/a11y';
import { afterEveryRender, ChangeDetectionStrategy, Component, ElementRef, EventEmitter, HostListener, inject, Input, OnChanges, Output, ViewChild } from '@angular/core';
import { IsActiveMatchOptions, Params, RouterModule } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { DxButtonModule } from 'devextreme-angular/ui/button';
import { DxTextBoxModule } from 'devextreme-angular/ui/text-box';
import { DxTemplateModule } from 'devextreme-angular/core';
import { VlpIconComponent } from '../../components/vlp-icon/vlp-icon.component';
import { ApplicationBreadcrumb, ApplicationNavigationItem, applicationRoute } from './application-shell.contracts';

/** Shared VLP/VFP renderer: no product services, credentials or scope resolution. */
@Component({
    selector: 'application-shell', standalone: true, changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, RouterModule, TranslocoModule, DxButtonModule, DxTextBoxModule, DxTemplateModule, VlpIconComponent, CdkTrapFocus],
    templateUrl: './application-shell.component.html', styleUrls: ['./application-shell.component.scss'],
})
export class ApplicationShellComponent implements OnChanges {
    @Input() linkQueryParams: Params | null = null;
    @Input() groups: readonly ApplicationNavigationItem[] = [];
    @Input() crumbs: readonly ApplicationBreadcrumb[] = [];
    @Input() collapsed = false;
    @Input() sidebarOpen = false;
    @Input() compact = false;
    @Input() menuFilter = '';
    @Input() collapsedGroups: ReadonlySet<string> = new Set();
    @Input() favoriteLinks: readonly string[] = [];
    @Input() exactRoutes: ReadonlySet<string> = new Set();
    @Input() accountName = '';
    @Input() workspaceName = '';
    @Input() workspaceMeta = '';
    @Input() selectableWorkspace = false;
    @Input() logoUrl = '';
    @Input() navLogoUrl = '';
    @Input() brandTitle = '';
    @Input() status: 'available' | 'degraded' | 'unavailable' | 'recovered' | 'neutral' = 'neutral';
    @Output() readonly sidebarToggle = new EventEmitter<void>();
    @Output() readonly sidebarClose = new EventEmitter<void>();
    @Output() readonly brandClick = new EventEmitter<void>();
    @Output() readonly groupToggle = new EventEmitter<string>();
    @Output() readonly menuFilterChange = new EventEmitter<string>();
    @Output() readonly favoriteToggle = new EventEmitter<string>();
    @Output() readonly viewportChanged = new EventEmitter<void>();

    @ViewChild('sidebar') private sidebar?: ElementRef<HTMLElement>;
    @ViewChild('sidebarNav') private sidebarNav?: ElementRef<HTMLElement>;
    @ViewChild('sidebarTrigger', { read: ElementRef }) private sidebarTrigger?: ElementRef<HTMLElement>;
    @ViewChild('mainPanel') private mainPanel?: ElementRef<HTMLElement>;
    private readonly document = inject(DOCUMENT);
    private readonly interactivity = inject(InteractivityChecker);
    private drawerWasOpen = false;
    private returnFocus: HTMLElement | null = null;

    constructor() {
        // Wait for visibility, inert and the CDK trap to reflect the new inputs before focusing.
        afterEveryRender(() => this.syncDrawerFocus());
    }

    ngOnChanges(): void {
        if (this.isDrawerOpen && !this.drawerWasOpen && !this.returnFocus) {
            const active = this.document.activeElement as HTMLElement | null;
            if (active && active !== this.document.body && !this.sidebar?.nativeElement.contains(active)) {
                this.returnFocus = active;
            }
        }
    }

    private syncDrawerFocus(): void {
        const open = this.isDrawerOpen;
        if (open === this.drawerWasOpen) return;
        this.drawerWasOpen = open;
        if (open) {
            // The navigation landmark is a stable initial target even with no permitted links.
            this.sidebarNav?.nativeElement.focus({ preventScroll: true });
            return;
        }
        const target = this.returnFocus ?? this.sidebarTrigger?.nativeElement;
        this.returnFocus = null;
        if (target?.isConnected && this.interactivity.isFocusable(target)) {
            target.focus({ preventScroll: true });
        } else if (this.isOverlaySidebar || this.compact) {
            // A route or compact-mode change can remove the original trigger.
            const fallback = this.sidebarTrigger?.nativeElement ?? this.mainPanel?.nativeElement;
            fallback?.focus({ preventScroll: true });
        }
        // On desktop the hamburger is hidden; retain focus in the now persistent navigation.
    }

    get isOverlaySidebar(): boolean { return typeof window !== 'undefined' && window.innerWidth <= 1024; }
    get isDrawerOpen(): boolean { return !this.compact && this.sidebarOpen && this.isOverlaySidebar; }
    readonly navigationLink = applicationRoute;
    readonly trackNavigation = (_index: number, item: ApplicationNavigationItem): string => item.id ?? item.link ?? item.title ?? '';
    readonly trackBreadcrumb = (_index: number, item: ApplicationBreadcrumb): string => `${item.url}:${item.label}`;
    private readonly exactActiveOptions = { exact: true };
    private readonly prefixActiveOptions = { exact: false };
    isGroupCollapsed(id?: string): boolean { return !this.menuFilter && this.collapsedGroups.has(id ?? ''); }
    isFavorite(link?: string): boolean { return this.favoriteLinks.includes(link ?? ''); }
    activeOptions(item: ApplicationNavigationItem): { exact: boolean } | IsActiveMatchOptions {
        const exact = item.exactMatch === true || this.exactRoutes.has(item.link ?? '') || this.exactRoutes.has(applicationRoute(item.link));
        // Product scope parameters do not change which navigation item represents the current page.
        if (this.linkQueryParams !== null) return { paths: exact ? 'exact' : 'subset', queryParams: 'ignored', matrixParams: 'ignored', fragment: 'ignored' };
        return exact ? this.exactActiveOptions : this.prefixActiveOptions;
    }
    toggleFavorite(link: string | undefined): void {
        if (link) this.favoriteToggle.emit(link);
    }
    @HostListener('window:resize') onResize(): void { this.viewportChanged.emit(); }
    @HostListener('document:keydown.escape', ['$event']) onEscape(event: KeyboardEvent): void {
        if (this.isDrawerOpen && !event.defaultPrevented) this.sidebarClose.emit();
    }
}
